import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import type { CameraSource } from './cameraSource';

interface LocalDevice {
  deviceId: string;
  label: string;
}

interface LocalCameraSource extends CameraSource {
  videoRef: RefObject<HTMLVideoElement | null>;
  /** The device the last live stream came from; undefined until one is live. */
  deviceId: string | undefined;
  devices: LocalDevice[];
  /** The open track's largest size; undefined where the browser reports none. */
  maxSize: { width: number; height: number } | undefined;
  /** The open track's zoom range; undefined where the browser has no zoom. */
  zoomRange: ZoomRange | undefined;
}

export interface ZoomRange {
  min: number;
  max: number;
  step: number;
}

// lib.dom has no zoom yet.
type ZoomConstraintSet = MediaTrackConstraintSet & { zoom?: number };
type ZoomCapabilities = MediaTrackCapabilities & {
  zoom?: { min: number; max: number; step?: number };
};

/**
 * The device's own cameras through `getUserMedia`, live while `active`. Without a
 * `deviceId` it opens the rear camera. `zoom` is applied to the open track without reopening it.
 */
export function useLocalCamera(
  active: boolean,
  deviceId?: string,
  zoom?: number,
): LocalCameraSource {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [liveDeviceId, setLiveDeviceId] = useState<string>();
  const [error, setError] = useState('');
  const [devices, setDevices] = useState<LocalDevice[]>([]);
  const [maxSize, setMaxSize] = useState<LocalCameraSource['maxSize']>();
  const [zoomRange, setZoomRange] = useState<ZoomRange>();

  useEffect(() => {
    // The previous stream is stopped by now, so the shutter must wait for the new one.
    setStream(null);
    setMaxSize(undefined);
    setZoomRange(undefined);
    if (!active) {
      setLiveDeviceId(undefined);
      return;
    }
    let live: MediaStream | null = null;
    let cancelled = false;
    const open = (source: MediaTrackConstraints) =>
      navigator.mediaDevices.getUserMedia({
        video: { ...source, width: { ideal: 4096 }, height: { ideal: 4096 } },
        audio: false,
      });
    const rear = { facingMode: 'environment' };
    // A saved camera that has since gone falls back to the rear one, so the lens list fills
    // and another can be chosen.
    (deviceId
      ? open({ deviceId: { exact: deviceId } }).catch((e: unknown) => {
          if (isMissing(e)) return open(rear);
          throw e;
        })
      : open(rear)
    )
      .then(async (s) => {
        if (cancelled) return stopStream(s);
        live = s;
        // Without ImageCapture the still is the preview frame, so the preview must be full size.
        const track = s.getVideoTracks()[0];
        if (!imageCaptureCtor()) await maximizeResolution(track);
        if (cancelled) return;
        // Unplugging the camera or the OS taking it ends the track without stopping the stream.
        track?.addEventListener('ended', () => {
          if (cancelled) return;
          setStream(null);
          setError('Camera disconnected. Choose from the gallery instead.');
        });
        const caps = capabilitiesOf(track);
        setMaxSize(caps.maxSize);
        setZoomRange(caps.zoomRange);
        setStream(s);
        setLiveDeviceId(track?.getSettings().deviceId);
        setError('');
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(cameraError(e));
      })
      // Device ids are blank before permission is granted, so list cameras only after asking.
      .then(() => navigator.mediaDevices.enumerateDevices())
      .then((all) => {
        if (cancelled) return;
        setDevices(
          all
            .filter((d) => d.kind === 'videoinput' && d.deviceId)
            .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Camera ${i + 1}` })),
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (live) stopStream(live);
    };
  }, [active, deviceId]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.srcObject = stream;
  }, [stream]);

  useEffect(() => {
    const track = stream?.getVideoTracks()[0];
    if (!track || !zoomRange || zoom === undefined) return;
    const clamped = Math.min(zoomRange.max, Math.max(zoomRange.min, zoom));
    // applyConstraints replaces the whole set, so the size constraints are carried over.
    const advanced: ZoomConstraintSet[] = [{ zoom: clamped }];
    // A camera that refuses stays at its current zoom.
    track.applyConstraints({ ...track.getConstraints(), advanced }).catch(() => {});
  }, [stream, zoomRange, zoom]);

  const capture = useCallback(async () => {
    const video = videoRef.current;
    if (!stream || !video) throw new Error('Could not take photo');
    return grabFrame(stream, video);
  }, [stream]);

  return {
    videoRef,
    ready: stream !== null,
    error,
    capture,
    deviceId: liveDeviceId,
    devices,
    maxSize,
    zoomRange,
  };
}

function capabilitiesOf(track: MediaStreamTrack | undefined): {
  maxSize: LocalCameraSource['maxSize'];
  zoomRange: ZoomRange | undefined;
} {
  // Firefox before 132 has no getCapabilities.
  if (!track || !('getCapabilities' in track)) return { maxSize: undefined, zoomRange: undefined };
  const caps: ZoomCapabilities = track.getCapabilities();
  const z = caps.zoom;
  return {
    maxSize:
      caps.width?.max && caps.height?.max
        ? { width: caps.width.max, height: caps.height.max }
        : undefined,
    zoomRange: z && z.max > z.min ? { min: z.min, max: z.max, step: z.step || 0.1 } : undefined,
  };
}

function stopStream(stream: MediaStream) {
  for (const track of stream.getTracks()) track.stop();
}

/**
 * Raises the track to its capability maximum, which `getUserMedia`'s `ideal` sizes can
 * settle below.
 */
export async function maximizeResolution(track: MediaStreamTrack | undefined): Promise<void> {
  // Firefox before 132 has no getCapabilities.
  if (!track || !('getCapabilities' in track)) return;
  const { width, height } = track.getCapabilities();
  if (!width?.max || !height?.max || (track.getSettings().width ?? 0) >= width.max) return;
  try {
    await track.applyConstraints({ width: { exact: width.max }, height: { exact: height.max } });
  } catch {
    // The camera stays at the size it opened at.
  }
}

type ImageCaptureCtor = new (track: MediaStreamTrack) => { takePhoto(): Promise<Blob> };

function imageCaptureCtor(): ImageCaptureCtor | undefined {
  return (window as { ImageCapture?: ImageCaptureCtor }).ImageCapture;
}

async function grabFrame(stream: MediaStream, video: HTMLVideoElement): Promise<Blob> {
  // ImageCapture (Chromium only) returns a full-resolution still; the preview frame is
  // the fallback everywhere else.
  const ImageCapture = imageCaptureCtor();
  const track = stream.getVideoTracks()[0];
  if (ImageCapture && track) {
    try {
      return await new ImageCapture(track).takePhoto();
    } catch {
      // Some cameras reject stills; the preview frame still works.
    }
  }
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext('2d')?.drawImage(video, 0, 0);
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Could not take photo'))),
      'image/jpeg',
      0.92,
    ),
  );
}

const isMissing = (e: unknown) =>
  e instanceof DOMException && (e.name === 'NotFoundError' || e.name === 'OverconstrainedError');

function cameraError(e: unknown): string {
  if (e instanceof DOMException && e.name === 'NotAllowedError')
    return 'Camera permission denied. Choose from the gallery instead.';
  if (isMissing(e)) return 'No camera found. Choose from the gallery instead.';
  return 'Camera unavailable. Choose from the gallery instead.';
}
